<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :z-index="10000"
    :width="860"
    :mask-closable="false"
    @cancel="onCancel"
  >
    <template slot="title">
      <span class="split-modal-title">
        <i class="fa fa-cut"></i>
        Split Order &mdash; {{ tableLabel }}
      </span>
    </template>

    <div class="split-quick-actions">
      <span class="split-quick-label">Quick split</span>
      <button type="button" class="quick-btn" :class="{ active: lastPreset === 25 }" @click="applyAll(25)">
        <span class="quick-label">25 / 75</span>
      </button>
      <button type="button" class="quick-btn quick-btn-even" :class="{ active: lastPreset === 50 }" @click="applyAll(50)">
        <span class="quick-label">50 / 50</span>
      </button>
      <button type="button" class="quick-btn" :class="{ active: lastPreset === 75 }" @click="applyAll(75)">
        <span class="quick-label">75 / 25</span>
      </button>
      <button type="button" class="quick-btn quick-btn-clear" @click="applyAll(0)">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div class="split-items-meta" v-if="rows.length > 0">
      <span>{{ rows.length }} item{{ rows.length > 1 ? 's' : '' }}</span>
      <span class="split-items-meta-hint">Scroll for more</span>
    </div>

    <div class="split-items">
      <div
        v-for="row in rows"
        :key="row.product_id + '_' + row.uom_id"
        class="split-item-card"
        :class="{ 'is-active': Number(row.percent_to_new) > 0 }"
      >
        <div class="item-row-top">
          <div class="item-info">
            <div class="item-name">{{ row.product_name || row.product_name_en || ('#' + row.product_id) }}</div>
            <div class="item-sub">
              {{ defaultCurrency }} {{ formatNum(row.unit_price) }}
              <span class="item-mul">×</span>
              {{ formatQty(row.quantity) }}
            </div>
          </div>
          <div class="item-row-actions">
            <button
              v-for="p in [25, 50, 75]"
              :key="p"
              type="button"
              class="item-quick"
              :class="{ active: Number(row.percent_to_new) === p }"
              @click="row.percent_to_new = p"
            >{{ p }}</button>
            <button
              type="button"
              class="item-quick item-quick-clear"
              :title="'Clear'"
              @click="row.percent_to_new = 0"
            ><i class="fa fa-times"></i></button>
            <div class="item-percent-tag" :class="{ 'is-active': Number(row.percent_to_new) > 0 }">
              {{ formatPct(row.percent_to_new) }}%
            </div>
          </div>
        </div>

        <div class="split-bar">
          <div
            class="split-bar-left"
            :style="{ width: (100 - Number(row.percent_to_new || 0)) + '%' }"
          >
            <span v-if="100 - Number(row.percent_to_new || 0) >= 12" class="bar-qty">
              {{ formatQty(qtyLeft(row)) }}
            </span>
          </div>
          <div
            class="split-bar-right"
            :style="{ width: Number(row.percent_to_new || 0) + '%' }"
          >
            <span v-if="Number(row.percent_to_new || 0) >= 12" class="bar-qty">
              {{ formatQty(qtyNew(row)) }}
            </span>
          </div>
        </div>

        <div class="split-controls">
          <a-slider
            class="split-slider"
            v-model="row.percent_to_new"
            :min="0"
            :max="100"
            :step="1"
            :tip-formatter="sliderTip"
          />
          <div class="split-percent-input">
            <a-input-number
              v-model="row.percent_to_new"
              :min="0"
              :max="100"
              :step="1"
              :precision="0"
              size="small"
            />
            <span class="percent-sign">%</span>
          </div>
        </div>
      </div>

      <div v-if="rows.length === 0" class="split-empty">
        <i class="fa fa-cut split-empty-icon"></i>
        <p>No splittable items on this bill.</p>
        <p class="split-empty-hint">Items already sent to the kitchen or part of a promotion can't be split.</p>
      </div>
    </div>

    <div class="split-sticky-footer" v-if="rows.length > 0">
      <div class="split-preview">
        <div class="preview-card preview-left" :class="{ 'is-active': canSubmit }">
          <div class="preview-head">
            <div class="preview-icon"><i class="fa fa-file-invoice"></i></div>
            <div>
              <div class="preview-title">This Bill <span class="preview-sub-inline">— Bill A</span></div>
            </div>
          </div>
          <div class="preview-amount">
            {{ defaultCurrency }} {{ formatNum(previewLeft.grand_total) }}
          </div>
          <div class="preview-meta">
            <span><i class="fa fa-list-ul"></i> {{ activeItemCount }} items</span>
            <span>VAT {{ defaultCurrency }} {{ formatNum(previewLeft.vat_amount) }}</span>
          </div>
        </div>
        <div class="preview-arrow">
          <i class="fa fa-arrow-right"></i>
        </div>
        <div class="preview-card preview-right" :class="{ 'is-active': canSubmit }">
          <div class="preview-head">
            <div class="preview-icon preview-icon-new"><i class="fa fa-file-invoice-dollar"></i></div>
            <div>
              <div class="preview-title">New Bill <span class="preview-sub-inline">— Bill B</span></div>
            </div>
          </div>
          <div class="preview-amount preview-amount-new">
            {{ defaultCurrency }} {{ formatNum(previewNew.grand_total) }}
          </div>
          <div class="preview-meta">
            <span><i class="fa fa-list-ul"></i> {{ activeItemCount }} items</span>
            <span>VAT {{ defaultCurrency }} {{ formatNum(previewNew.vat_amount) }}</span>
          </div>
        </div>
      </div>

      <div class="split-actions">
        <ts-button @click.prevent="onCancel">Cancel</ts-button>
        <ts-button
          color="primary"
          :waiting="waiting"
          :disabled="!canSubmit"
          @click.prevent="onSubmit"
          class="confirm-btn"
        >
          <i class="fa fa-cut"></i> Split into 2 bills
        </ts-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
export default {
  name: 'SplitOrderModal',
  props: {
    visible: { type: Boolean, default: false },
    tableSelected: { type: Object, default: () => ({}) },
    invoiceDetail: { type: Array, default: () => [] },
    defaultCurrency: { type: String, default: '' }
  },
  data() {
    return {
      waiting: false,
      rows: [],
      lastPreset: null
    }
  },
  computed: {
    tableLabel() {
      return this.tableSelected && this.tableSelected.table_no
        ? 'Field ' + this.tableSelected.table_no
        : ''
    },
    canSubmit() {
      const hasMoveOut = this.rows.some(r => Number(r.percent_to_new) > 0)
      const hasLeftBehind = this.rows.some(r => Number(r.percent_to_new) < 100)
      return hasMoveOut && hasLeftBehind
    },
    activeItemCount() {
      return this.rows.filter(r => Number(r.percent_to_new) > 0).length
    },
    previewNew() {
      return this.rows.reduce(
        (acc, r) => {
          const p = Number(r.percent_to_new) / 100
          acc.sub_total += Number(r.quantity || 0) * Number(r.unit_price || 0) * p
          acc.vat_amount += Number(r.vat_amount || 0) * p
          acc.grand_total += Number(r.grand_total || 0) * p
          return acc
        },
        { sub_total: 0, vat_amount: 0, grand_total: 0 }
      )
    },
    previewLeft() {
      return this.rows.reduce(
        (acc, r) => {
          const p = Number(r.percent_to_new) / 100
          acc.sub_total += Number(r.quantity || 0) * Number(r.unit_price || 0) * (1 - p)
          acc.vat_amount += Number(r.vat_amount || 0) * (1 - p)
          acc.grand_total += Number(r.grand_total || 0) * (1 - p)
          return acc
        },
        { sub_total: 0, vat_amount: 0, grand_total: 0 }
      )
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetRows()
        this.lastPreset = null
      }
    }
  },
  methods: {
    resetRows() {
      this.rows = (this.invoiceDetail || [])
        .filter(d => d && d.product_id && d.uom_id)
        .filter(d => !d.is_allow_adjust_free_quantity)
        .map(d => ({
          product_id: d.product_id,
          uom_id: d.uom_id,
          product_name: d.product_name || d.product_name_en || d.product_name_kh,
          product_name_en: d.product_name_en,
          quantity: Number(d.quantity || 0),
          unit_price: Number(d.unit_price || 0),
          vat_amount: Number(d.vat_amount || 0),
          grand_total: Number(d.grand_total || 0),
          percent_to_new: 50
        }))
        .filter(r => r.quantity > 0)
      this.lastPreset = this.rows.length > 0 ? 50 : null
    },
    applyAll(percent) {
      this.rows.forEach(r => { r.percent_to_new = percent })
      this.lastPreset = percent === 0 ? null : percent
    },
    qtyNew(row) {
      return Number(row.quantity || 0) * Number(row.percent_to_new || 0) / 100
    },
    qtyLeft(row) {
      return Number(row.quantity || 0) - this.qtyNew(row)
    },
    formatQty(n) {
      return Number(n || 0).toFixed(2)
    },
    formatNum(n) {
      return Number(n || 0).toFixed(2)
    },
    formatPct(n) {
      return Number(n || 0).toFixed(0)
    },
    sliderTip(val) {
      return val + '%'
    },
    onCancel() {
      if (this.waiting) return
      this.$emit('cancel')
    },
    onSubmit() {
      if (!this.canSubmit) return
      const lines = this.rows
        .filter(r => Number(r.percent_to_new) > 0)
        .map(r => ({
          product_id: r.product_id,
          uom_id: r.uom_id,
          percent_to_new: Number(r.percent_to_new)
        }))

      this.waiting = true
      this.$store
        .dispatch('restaurantSale/counterCheckout/splitOrder', {
          stock_out_id: this.tableSelected.stock_out_id,
          lines
        })
        .then(response => {
          this.$toasted.success(response.message || 'Order split successfully')
          this.$emit('success', response)
        })
        .catch(error => {
          this.$toasted.error((error && error.message) || 'Failed to split order')
        })
        .finally(() => {
          this.waiting = false
        })
    }
  }
}
</script>

<style scoped>
.split-modal-title {
  color: #2563eb;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
}

/* Quick split buttons */
.split-quick-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
}
.split-quick-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 4px;
}
.quick-btn {
  flex: 1;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
}
.quick-btn:hover {
  border-color: #60a5fa;
  color: #1d4ed8;
}
.quick-btn.active {
  background: #dbeafe;
  border-color: #2563eb;
  color: #1d4ed8;
}
.quick-btn-even {
  font-weight: 700;
}
.quick-btn-clear {
  flex: 0 0 36px;
  color: #9ca3af;
}
.quick-btn-clear:hover {
  border-color: #ef4444;
  color: #ef4444;
}

/* Items meta */
.split-items-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.split-items-meta-hint { color: #9ca3af; font-weight: 500; }

/* Item cards */
.split-items {
  max-height: 520px;
  overflow-y: auto;
  margin-bottom: 12px;
  padding-right: 4px;
}
.split-item-card {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px 8px 12px;
  background: #fff;
  margin-bottom: 6px;
  transition: all 0.15s;
}
.split-item-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  border-radius: 8px 0 0 8px;
  background: transparent;
  transition: background 0.15s;
}
.split-item-card.is-active {
  border-color: #cbd5e1;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.split-item-card.is-active::before {
  background: #2563eb;
}
.item-row-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.item-info { min-width: 0; flex: 1; }
.item-name {
  font-weight: 600;
  font-size: 12px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}
.item-sub {
  font-size: 10px;
  color: #6b7280;
  margin-top: 1px;
}
.item-mul { color: #d1d5db; margin: 0 2px; }

/* Per-item quick split */
.item-row-actions {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}
.item-quick {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 6px;
  height: 22px;
  min-width: 26px;
  padding: 0 6px;
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.12s;
}
.item-quick:hover {
  border-color: #60a5fa;
  color: #2563eb;
}
.item-quick.active {
  background: #dbeafe;
  border-color: #2563eb;
  color: #1d4ed8;
}
.item-quick-clear { color: #9ca3af; min-width: 22px; padding: 0 4px; }
.item-quick-clear:hover { border-color: #ef4444; color: #ef4444; }

.item-percent-tag {
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 2px 8px;
  min-width: 44px;
  text-align: center;
  transition: all 0.15s;
  margin-left: 4px;
}
.item-percent-tag.is-active {
  color: #fff;
  background: linear-gradient(135deg, #2563eb, #60a5fa);
}

/* Visual split bar — minimal */
.split-bar {
  display: flex;
  height: 16px;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 6px;
  background: #f9fafb;
  border: 1px solid #f1f5f9;
}
.split-bar-left {
  background: #dcfce7;
  color: #166534;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  transition: width 0.2s ease;
  min-width: 0;
}
.split-bar-right {
  background: #dbeafe;
  color: #1e40af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  transition: width 0.2s ease;
  min-width: 0;
}
.bar-qty {
  white-space: nowrap;
  overflow: hidden;
}

/* Controls */
.split-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}
.split-slider { flex: 1; margin: 0; }
.split-percent-input {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 64px;
}
.split-percent-input .ant-input-number { width: 50px; }
.percent-sign {
  color: #6b7280;
  font-weight: 600;
  font-size: 11px;
}

/* Empty state */
.split-empty {
  text-align: center;
  padding: 32px 16px;
  color: #9ca3af;
}
.split-empty-icon {
  font-size: 32px;
  margin-bottom: 10px;
  display: block;
  opacity: 0.5;
}
.split-empty-hint {
  font-size: 11px;
  color: #d1d5db;
  margin-top: 4px;
}

/* Sticky footer wrapping preview + actions */
.split-sticky-footer {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding-top: 10px;
  margin: 0 -24px -10px;
  padding: 12px 24px 6px;
  border-top: 1px solid #f1f1f1;
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.03);
}

/* Preview cards */
.split-preview {
  display: grid;
  grid-template-columns: 1fr 24px 1fr;
  gap: 8px;
  align-items: stretch;
  margin-bottom: 10px;
}
.preview-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  background: #f9fafb;
  transition: all 0.2s;
}
.preview-card.is-active.preview-left {
  background: #f0fdf4;
  border-color: #bbf7d0;
}
.preview-card.is-active.preview-right {
  background: #eff6ff;
  border-color: #dbeafe;
}
.preview-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.preview-icon {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: #dcfce7;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}
.preview-icon-new {
  background: #dbeafe;
  color: #2563eb;
}
.preview-title {
  font-size: 11px;
  font-weight: 700;
  color: #111;
  line-height: 1.2;
}
.preview-sub-inline {
  color: #9ca3af;
  font-weight: 500;
}
.preview-amount {
  font-size: 18px;
  font-weight: 800;
  color: #15803d;
  margin-bottom: 2px;
  line-height: 1.1;
}
.preview-amount-new { color: #1d4ed8; }
.preview-meta {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #6b7280;
}
.preview-meta .fa { margin-right: 3px; }
.preview-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d1d5db;
  font-size: 14px;
}

/* Actions */
.split-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.confirm-btn .fa { margin-right: 4px; }

/* Slider track tint */
.split-slider >>> .ant-slider-track {
  background: linear-gradient(135deg, #2563eb, #60a5fa) !important;
}
.split-slider >>> .ant-slider-handle {
  border-color: #2563eb !important;
}
.split-slider >>> .ant-slider-handle:focus {
  box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.12) !important;
}
.split-slider >>> .ant-slider-rail {
  background: linear-gradient(to right, #bbf7d0 0%, #bbf7d0 100%) !important;
}
</style>
